import React, { useMemo, useState } from 'react';
import { useAdminGamification } from '../../hooks/useAdminGamification.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const formatSync = (value) => {
  if (!value) return 'Sin sincronización';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sincronización registrada';
  return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMoney = (value) => {
  const parsed = Number(value || 0);
  return parsed.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0
  });
};

const GamificationSection = ({ onNotify }) => {
  const {
    levels,
    kpis,
    status,
    salesSummary,
    lastSalesSyncResult,
    salesData,
    loading,
    saving,
    salesSyncing,
    salesDataLoading,
    error,
    updateLevel,
    updateKpi,
    saveLevels,
    saveKpis,
    uploadLevelImage,
    syncSales,
    loadSalesData,
    reload
  } = useAdminGamification();
  const defaultSalesRange = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 89);
    return { startDate: toDateInput(start), endDate: toDateInput(end) };
  }, []);
  const [salesRange, setSalesRange] = useState(defaultSalesRange);

  useLucideIcons([levels.length, kpis.length, loading, saving, status?.status, salesSyncing, salesDataLoading, salesSummary?.sales_rows, salesData.rows?.length]);

  const handleSaveLevels = async () => {
    try {
      await saveLevels();
      onNotify?.({ type: 'success', message: 'Niveles guardados.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron guardar niveles.' });
    }
  };

  const handleSaveKpis = async () => {
    try {
      await saveKpis();
      onNotify?.({ type: 'success', message: 'KPIs guardados.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron guardar KPIs.' });
    }
  };

  const handleImage = async (levelId, file) => {
    try {
      await uploadLevelImage(levelId, file);
      onNotify?.({ type: 'success', message: 'Imagen de nivel actualizada.' });
      await reload();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo subir la imagen.' });
    }
  };

  const handleSalesSync = async () => {
    try {
      const result = await syncSales(salesRange);
      onNotify?.({
        type: 'success',
        message: `Ventas sincronizadas: ${result.rows || 0} filas.`
      });
      await reload();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron sincronizar ventas.' });
    }
  };

  const handleLoadSalesData = async () => {
    try {
      await loadSalesData(salesRange);
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron consultar ventas guardadas.' });
    }
  };

  return (
    <section id="gamification-section" className="admin-section active gamification-admin-section">
      <div className="section-header">
        <div className="section-title-group">
          <div className="section-title-line">
            <h2 className="section-title">Gamificación</h2>
          </div>
        </div>
        <button className="admin-action-btn secondary" type="button" onClick={reload} disabled={loading}>
          <i data-lucide={loading ? 'loader' : 'refresh-cw'} />
          Actualizar
        </button>
      </div>

      {error ? <div className="admin-error-banner">{error}</div> : null}

      <div className="gamification-admin-status">
        <div>
          <span>Última sincronización</span>
          <strong>{formatSync(status?.fetched_at)}</strong>
        </div>
        <div>
          <span>Estado</span>
          <strong>{status?.status || 'N/D'}</strong>
        </div>
        <div>
          <span>Filas</span>
          <strong>{status?.rows_count ?? 0}</strong>
        </div>
      </div>

      <div className="gamification-admin-layout">
        <div className="gamification-admin-panel gamification-sales-panel">
          <div className="panel-heading">
            <h3>Ventas Metabase</h3>
            <div className="panel-actions-inline">
              <button className="admin-action-btn secondary" type="button" onClick={handleLoadSalesData} disabled={salesDataLoading}>
                <i data-lucide={salesDataLoading ? 'loader' : 'table'} />
                {salesDataLoading ? 'Cargando' : 'Ver data guardada'}
              </button>
              <button className="admin-action-btn" type="button" onClick={handleSalesSync} disabled={salesSyncing}>
                <i data-lucide={salesSyncing ? 'loader' : 'database'} />
                {salesSyncing ? 'Sincronizando' : 'Sincronizar ventas'}
              </button>
            </div>
          </div>
          <div className="gamification-sales-grid">
            <label>
              <span>Fecha inicio</span>
              <input
                type="date"
                value={salesRange.startDate}
                onChange={(event) => setSalesRange((prev) => ({ ...prev, startDate: event.target.value }))}
              />
            </label>
            <label>
              <span>Fecha fin</span>
              <input
                type="date"
                value={salesRange.endDate}
                onChange={(event) => setSalesRange((prev) => ({ ...prev, endDate: event.target.value }))}
              />
            </label>
            <div className="sales-summary-tile">
              <span>Filas guardadas</span>
              <strong>{salesSummary?.sales_rows || 0}</strong>
            </div>
            <div className="sales-summary-tile">
              <span>Venta neta guardada</span>
              <strong>{formatMoney(salesSummary?.net_sales_total)}</strong>
            </div>
            <div className="sales-summary-tile">
              <span>Rango guardado</span>
              <strong>{salesSummary?.first_sales_date || '-'} / {salesSummary?.last_sales_date || '-'}</strong>
            </div>
          </div>
          {lastSalesSyncResult?.diagnostics ? (
            <div className="sales-sync-diagnostics">
              <span>
                Fuente Metabase: {lastSalesSyncResult.diagnostics.source_rows || 0} filas,
                {lastSalesSyncResult.diagnostics.sampled_rows_with_date || 0} de muestra con fecha.
              </span>
              <code>{(lastSalesSyncResult.diagnostics.normalized_headers || []).join(', ') || 'Sin headers'}</code>
            </div>
          ) : null}
          {salesData.loaded ? (
            <div className="sales-data-viewer">
              <div className="sales-data-viewer-header">
                <span>
                  Mostrando {salesData.rows.length} de {salesData.totals?.rows_count || 0} filas.
                </span>
                <strong>{formatMoney(salesData.totals?.net_sales_total)}</strong>
              </div>
              <div className="sales-data-table-wrap">
                <table className="sales-data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Restaurante</th>
                      <th>RBI</th>
                      <th>Venta neta</th>
                      <th>Actualizado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.rows.length ? salesData.rows.map((row) => (
                      <tr key={`${row.operation_date}-${row.restaurant_id || row.rbi_id}-${row.restaurant_name}`}>
                        <td>{row.operation_date || '-'}</td>
                        <td>{row.restaurant_name || '-'}</td>
                        <td>{row.rbi_id || row.restaurant_id || '-'}</td>
                        <td>{formatMoney(row.net_sales)}</td>
                        <td>{formatSync(row.sales_updated_at)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5">No hay ventas guardadas en el rango seleccionado.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        <div className="gamification-admin-panel">
          <div className="panel-heading">
            <h3>Niveles</h3>
            <button className="admin-action-btn" type="button" onClick={handleSaveLevels} disabled={saving}>
              <i data-lucide="save" />
              Guardar niveles
            </button>
          </div>
          <div className="gamification-level-editor">
            {levels.map((level) => (
              <div className="gamification-level-editor-row" key={level.id}>
                <div className="level-preview" style={{ '--preview-color': level.color_hex || '#C8102E', '--preview-text': level.text_color_hex || '#FFFFFF' }}>
                  {level.image_url ? <img src={level.image_url} alt={level.nombre} /> : <span />}
                </div>
                <input value={level.nombre || ''} onChange={(event) => updateLevel(level.id, 'nombre', event.target.value)} />
                <input type="color" value={level.color_hex || '#C8102E'} onChange={(event) => updateLevel(level.id, 'color_hex', event.target.value)} />
                <input type="number" value={level.sort_order || 0} onChange={(event) => updateLevel(level.id, 'sort_order', Number(event.target.value))} />
                <input value={level.descripcion || ''} onChange={(event) => updateLevel(level.id, 'descripcion', event.target.value)} />
                <label className="image-upload-button">
                  <i data-lucide="image-plus" />
                  <input type="file" accept="image/*" onChange={(event) => handleImage(level.id, event.target.files?.[0])} />
                </label>
                <label className="switch-inline">
                  <input type="checkbox" checked={Boolean(level.activo)} onChange={(event) => updateLevel(level.id, 'activo', event.target.checked)} />
                  Activo
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="gamification-admin-panel">
          <div className="panel-heading">
            <h3>KPIs y medición</h3>
            <button className="admin-action-btn" type="button" onClick={handleSaveKpis} disabled={saving}>
              <i data-lucide="save" />
              Guardar KPIs
            </button>
          </div>
          <div className="gamification-kpi-table">
            {kpis.map((kpi) => (
              <div className="gamification-kpi-row" key={kpi.id}>
                <input value={kpi.nombre || ''} onChange={(event) => updateKpi(kpi.id, 'nombre', event.target.value)} />
                <select value={kpi.comparator || '>='} onChange={(event) => updateKpi(kpi.id, 'comparator', event.target.value)}>
                  <option value=">=">&gt;=</option>
                  <option value="<=">&lt;=</option>
                  <option value="=">=</option>
                </select>
                <input type="number" value={kpi.target_value ?? ''} onChange={(event) => updateKpi(kpi.id, 'target_value', event.target.value)} />
                <input value={kpi.unit_label || ''} onChange={(event) => updateKpi(kpi.id, 'unit_label', event.target.value)} />
                <textarea value={kpi.descripcion || ''} onChange={(event) => updateKpi(kpi.id, 'descripcion', event.target.value)} />
                <label className="switch-inline">
                  <input type="checkbox" checked={Boolean(kpi.activo)} onChange={(event) => updateKpi(kpi.id, 'activo', event.target.checked)} />
                  Activo
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GamificationSection;
